/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  Icon,
  Tooltip
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import api from "utils/api";
import { formatDate } from "utils/dateFormatter";

const PlakaGecmisModal = ({ isOpen, onClose, aracData }) => {
  const [plakaGecmisi, setPlakaGecmisi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Modal açıldığında plaka geçmişini yükle
  useEffect(() => {
    if (isOpen && aracData) {
      loadPlakaGecmisi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aracData]);

  // Plaka geçmişini API'den çek
  const loadPlakaGecmisi = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(
        `/api/devices/by-imei/${aracData.imei}/plate-history`
      );

      if (!response.ok) {
        throw new Error('Plaka geçmişi yüklenemedi');
      }

      const data = await response.json();
      setPlakaGecmisi(Array.isArray(data) ? data : []);

    } catch (error) {
      setError(error.message || 'Plaka geçmişi yüklenirken bir hata oluştu');
      setPlakaGecmisi([]);
    } finally {
      setLoading(false);
    }
  };


  // Değişiklik türüne göre badge rengi
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'aktif':
      case 'güncel':
        return 'green';
      case 'changed':
      case 'değiştirildi':
      case 'eski':
        return 'orange';
      case 'deleted':
      case 'silindi':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Modal kapanırken temizlik
  const handleClose = () => {
    setPlakaGecmisi([]);
    setError("");
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="xl"
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Plaka Geçmişi
            </Text>
            <Text fontSize="xs" color="gray.400">
              {aracData?.plaka} • {aracData?.firma_ismi || aracData?.firma}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          <VStack spacing={4} align="stretch">
            {/* Mevcut araç bilgileri */}
            <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="blue.800" fontWeight="semibold">Güncel Plaka:</Text>
                  <Badge colorScheme="blue" variant="solid">{aracData?.plaka}</Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="blue.700">IMEI:</Text>
                  <Text fontSize="sm" color="blue.700">{aracData?.imei}</Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Loading durumu */}
            {loading && (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" color="blue.500" />
                <Text mt={3} color="gray.600">Plaka geçmişi yükleniyor...</Text>
              </Box>
            )}

            {/* Hata durumu */}
            {error && !loading && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Plaka geçmişi tablosu */}
            {!loading && !error && (
              <>
                {plakaGecmisi.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <Icon as={InfoIcon} boxSize={8} color="gray.400" mb={3} />
                    <Text color="gray.600">Henüz plaka değişikliği kaydı bulunamadı</Text>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr bg="gray.50">
                          <Th fontSize="xs">Plaka</Th>
                          <Th fontSize="xs">Durum</Th>
                          <Th fontSize="xs">Değişim Tarihi</Th>
                          <Th fontSize="xs">Kullanım Süresi</Th>
                          <Th fontSize="xs">Değiştiren</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {plakaGecmisi.map((gecmis, index) => (
                          <Tr key={index} _hover={{ bg: "gray.50" }}>
                            <Td fontWeight="semibold">
                              {gecmis.plaka}
                            </Td>
                            <Td>
                              <Badge 
                                colorScheme={getStatusColor(gecmis.durum)} 
                                variant="subtle"
                                fontSize="xs"
                              >
                                {gecmis.durum || 'Eski'}
                              </Badge>
                            </Td>
                            <Td fontSize="xs" color="gray.600">
                              {formatDate(gecmis.degisim_tarihi)}
                            </Td>
                            <Td fontSize="xs" color="gray.600">
                              {gecmis.kullanim_suresi || '-'}
                            </Td>
                            <Td fontSize="xs" color="gray.600">
                              <Tooltip label={gecmis.degistiren_kisi_detay} hasArrow>
                                <Text cursor="help">
                                  {gecmis.degistiren_kisi || 'Sistem'}
                                </Text>
                              </Tooltip>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}

                {/* İstatistikler */}
                {plakaGecmisi.length > 0 && (
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <HStack justify="space-around" textAlign="center">
                      <VStack spacing={1}>
                        <Text fontSize="lg" fontWeight="bold" color="blue.600">
                          {plakaGecmisi.length + 1}
                        </Text>
                        <Text fontSize="xs" color="gray.600">Toplam Plaka</Text>
                      </VStack>
                      <VStack spacing={1}>
                        <Text fontSize="lg" fontWeight="bold" color="green.600">
                          {plakaGecmisi.length}
                        </Text>
                        <Text fontSize="xs" color="gray.600">Değişiklik</Text>
                      </VStack>
                      <VStack spacing={1}>
                        <Text fontSize="lg" fontWeight="bold" color="orange.600">
                          {formatDate(plakaGecmisi[0]?.degisim_tarihi).split(' ')[0]}
                        </Text>
                        <Text fontSize="xs" color="gray.600">Son Değişim</Text>
                      </VStack>
                    </HStack>
                  </Box>
                )}
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <HStack spacing={3}>
            <Button 
              variant="ghost" 
              onClick={loadPlakaGecmisi}
              isLoading={loading}
              size="sm"
            >
              Yenile
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleClose}
              size="sm"
            >
              Kapat
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PlakaGecmisModal;
